// M8-ASSESS: the questionnaire a respondent answers, as data. Two templates, the X-Ray scan
// matrix (founder rule: hours, tasks, energy and role clarity; never salary): `full` (the
// quarterly full audit: role and clarity, tasks with their ratings, the week, management for
// managers, friction) and `delta` (the monthly quick scan: the same without the role step). The
// steps are the member flow of `member/memberAudit` (`memberStepFlow`), and every answer is
// checked with its validators, so the questionnaire, the API and the report agree on one shape.
//
// New beside the lifted flow (x-ray.md §9.1 #1): each task also carries its pillar (the area of
// work), so the hire signal and the proposed seats can tell Operations from Sales.
import { PILLARS } from "../engine/pillars";
import {
  ENERGY_CHOICES,
  ENJOY_CHOICES,
  type EnjoyChoice,
  FRICTION_FIELDS,
  isLoadBand,
  LOAD_BAND_LABELS,
  LOAD_BANDS,
  MEMBER_STEP,
  memberStepFlow,
  ROLE_CLARITY_ITEMS,
  SKILL_CHOICES,
  toEnjoymentRating,
  validateCapacity,
  validateFriction,
  validateHoursPerWeek,
  validateRoleClarity,
  validateRoleContext,
  validateWhy,
} from "../member/memberAudit";
import type { TeamActivityInput, TeamMemberInput } from "../team/teamAggregation";

export const QUESTIONNAIRE_KEYS = ["full", "delta"] as const;
export type QuestionnaireKey = (typeof QUESTIONNAIRE_KEYS)[number];

export type QuestionnaireStep =
  | "role"
  | "tasks"
  | "ratings"
  | "capacity"
  | "management"
  | "friction";

const STEP_OF: Record<number, QuestionnaireStep> = {
  [MEMBER_STEP.SEAT]: "role",
  [MEMBER_STEP.TASKS]: "tasks",
  [MEMBER_STEP.RATINGS]: "ratings",
  [MEMBER_STEP.CAPACITY]: "capacity",
  [MEMBER_STEP.MANAGEMENT]: "management",
  [MEMBER_STEP.FRICTION]: "friction",
};

export type QuestionnaireTemplate = {
  key: QuestionnaireKey;
  label: string;
  description: string;
  /** Minutes a respondent typically needs. */
  minutes: number;
};

export const QUESTIONNAIRES: Record<QuestionnaireKey, QuestionnaireTemplate> = {
  full: {
    key: "full",
    label: "Full team X-Ray",
    description:
      "Role and role clarity, every task with its hours, energy and fit, the week's load, and what falls through the cracks. Quarterly.",
    minutes: 15,
  },
  delta: {
    key: "delta",
    label: "Quick scan",
    description:
      "Tasks, hours, energy and load only: what changed since the last round. Monthly, about 5 minutes.",
    minutes: 5,
  },
};

/** The steps a respondent walks, in order (managers get the management step). */
export function questionnaireSteps(key: QuestionnaireKey, isManager: boolean): QuestionnaireStep[] {
  return memberStepFlow(isManager, key === "delta").map((s) => STEP_OF[s] as QuestionnaireStep);
}

export const PILLAR_CHOICES = Object.keys(PILLARS)
  .filter((k) => k !== "Administrative & Internal")
  .map((k) => ({ value: k, label: k === "Non Strategic" ? "Admin & other" : k }));

export const CLARITY_QUESTIONS: Record<(typeof ROLE_CLARITY_ITEMS)[number], string> = {
  responsibilities: "I know exactly what I am responsible for.",
  success_definition: "I know what doing my job well looks like.",
  pulled_into_others: "I get pulled into work that isn't mine.",
  handoff_clarity: "Hand-offs to and from me are clear.",
};

export const FRICTION_QUESTIONS: Record<(typeof FRICTION_FIELDS)[number], string> = {
  cracks: "What falls through the cracks between people?",
  invisible: "What do you do that nobody sees?",
  drop: "What would you stop doing tomorrow if you could?",
};

/** Everything a respondent's page needs to render the questions; no answers. */
export const QUESTION_CHOICES = {
  energy: ENERGY_CHOICES.map((c) => ({ value: c.value, label: c.label })),
  enjoy: ENJOY_CHOICES.map((c) => ({ value: c.value, label: c.label })),
  skill: SKILL_CHOICES.map((c) => ({ value: c.value, label: c.label })),
  pillar: PILLAR_CHOICES,
  load_band: LOAD_BANDS.map((b) => ({ value: b, ...LOAD_BAND_LABELS[b] })),
  clarity: ROLE_CLARITY_ITEMS.map((k) => ({ key: k, label: CLARITY_QUESTIONS[k] })),
  friction: FRICTION_FIELDS.map((k) => ({ key: k, label: FRICTION_QUESTIONS[k] })),
};

// Answers ---------------------------------------------------------------------------------------

export type TaskAnswer = {
  name: string;
  hours_per_week: number | null;
  energy: number | null; // -2..2
  enjoy: EnjoyChoice | null;
  skill: string | null; // Basic | Intermediate | Advanced
  pillar: string | null;
  /** "Someone else should be doing this" (the engine's `force_delegation`). */
  someone_else: boolean;
  why: string | null;
};

export type Answers = {
  role_title: string | null;
  clarity: Partial<Record<(typeof ROLE_CLARITY_ITEMS)[number], number>> | null;
  tasks: TaskAnswer[];
  capacity_hours: number | null;
  load_band: string | null;
  management: {
    mgmt_share: number | null;
    team_load_read: string | null;
    team_clarity_read: number | null;
    meeting_overhead_hours: number | null;
    bottleneck: string | null;
  } | null;
  friction: { cracks: string; invisible: string; drop: string } | null;
};

export const EMPTY_ANSWERS: Answers = {
  role_title: null,
  clarity: null,
  tasks: [],
  capacity_hours: null,
  load_band: null,
  management: null,
  friction: null,
};

export const MAX_TASKS = 30;

export type AnswerError = { path: string; message: string };

/**
 * Whether answers are complete enough to submit for this questionnaire. A draft may be saved
 * half-done; a submission must pass every step the respondent walks (the lifted validators).
 */
export function validateSubmission(
  key: QuestionnaireKey,
  isManager: boolean,
  a: Answers,
): AnswerError[] {
  const errors: AnswerError[] = [];
  const steps = questionnaireSteps(key, isManager);
  if (steps.includes("role")) {
    const r = validateRoleClarity({ role_title: a.role_title ?? "", items: a.clarity ?? {} });
    if (!r.ok) errors.push({ path: "clarity", message: r.error });
  }
  const named = a.tasks.filter((t) => t.name.trim().length > 0);
  if (named.length === 0) errors.push({ path: "tasks", message: "List at least one task" });
  if (named.length > MAX_TASKS)
    errors.push({ path: "tasks", message: `At most ${MAX_TASKS} tasks` });
  named.forEach((t, i) => {
    const p = `tasks.${i}`;
    if (validateHoursPerWeek(t.hours_per_week) === null)
      errors.push({ path: `${p}.hours_per_week`, message: "Hours per week between 0.5 and 80" });
    if (t.energy === null || !Number.isInteger(t.energy) || t.energy < -2 || t.energy > 2)
      errors.push({ path: `${p}.energy`, message: "Pick how this task leaves you" });
    if (!t.enjoy || !ENJOY_CHOICES.some((c) => c.value === t.enjoy))
      errors.push({ path: `${p}.enjoy`, message: "Pick how much you enjoy it" });
    if (!t.skill || !SKILL_CHOICES.some((c) => c.value === t.skill))
      errors.push({ path: `${p}.skill`, message: "Pick how good you are at it" });
    if (!t.pillar || !PILLAR_CHOICES.some((c) => c.value === t.pillar))
      errors.push({ path: `${p}.pillar`, message: "Pick the area this work is in" });
    if (key === "full") {
      const w = validateWhy(t.why);
      if (!w.ok) errors.push({ path: `${p}.why`, message: w.error });
    }
  });
  const cap = validateCapacity({ capacity_hours: a.capacity_hours, load_band: a.load_band });
  if (!cap.ok) errors.push({ path: "capacity", message: cap.error });
  if (steps.includes("management")) {
    const m = validateRoleContext(a.management ?? {});
    if (!m.ok) errors.push({ path: "management", message: m.error });
  }
  if (steps.includes("friction")) {
    const f = validateFriction(a.friction ?? {});
    if (!f.ok) errors.push({ path: "friction", message: f.error });
  }
  return errors;
}

/** How far a draft has got: answered steps out of the steps it walks (0..1). */
export function answeredShare(key: QuestionnaireKey, isManager: boolean, a: Answers): number {
  const steps = questionnaireSteps(key, isManager);
  const done = steps.filter((s) => {
    switch (s) {
      case "role":
        return !!a.role_title && Object.keys(a.clarity ?? {}).length === ROLE_CLARITY_ITEMS.length;
      case "tasks":
        return a.tasks.some((t) => t.name.trim());
      case "ratings":
        return a.tasks.length > 0 && a.tasks.every((t) => t.hours_per_week && t.energy !== null);
      case "capacity":
        return !!a.capacity_hours && isLoadBand(a.load_band);
      case "management":
        return !!a.management;
      case "friction":
        return !!a.friction && FRICTION_FIELDS.some((k) => (a.friction?.[k] ?? "").trim());
    }
    return false;
  }).length;
  return steps.length ? done / steps.length : 0;
}

// To the engine ---------------------------------------------------------------------------------

export type RespondentInput = {
  participant_id: string;
  name: string;
  role_title: string | null;
  is_owner: boolean;
  /** The participant this one reports to (from the seat chart, or set on a guest). */
  reports_to_id: string | null;
  completed: boolean;
  answers: Answers | null;
};

export function toActivity(t: TaskAnswer): TeamActivityInput {
  return {
    activity_name: t.name.trim(),
    hours_per_week: t.hours_per_week,
    energy_impact: t.energy,
    skill: t.skill,
    enjoyment_rating: t.enjoy ? toEnjoymentRating(t.enjoy, t.skill) : null,
    strategic_pillar: t.pillar,
    force_delegation: t.someone_else,
    task_why: t.why,
  };
}

/** One respondent as the team aggregation reads it; non-respondents carry no data. */
export function toTeamMember(r: RespondentInput): TeamMemberInput {
  const a = r.completed ? r.answers : null;
  return {
    member_id: r.participant_id,
    name: r.name,
    role_title: a?.role_title ?? r.role_title,
    is_owner: r.is_owner,
    participated: !!a,
    activities: a ? a.tasks.filter((t) => t.name.trim()).map(toActivity) : [],
    capacity_hours: a?.capacity_hours ?? null,
    load_band: a?.load_band ?? null,
    role_clarity: a?.clarity ? { items: a.clarity } : null,
    friction: a?.friction ?? null,
    reports_to_id: r.reports_to_id,
    role_context: a?.management ?? null,
  };
}
